import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaFileExcel, FaPrint } from "react-icons/fa";
import { useCan } from "../../../authz/useCan";
import { exportReport } from "../api";
import { reportErrorMessage } from "../errors";
import { downloadReportExcel } from "../excelExport";
import type { Catalog, ExportFormat, ReportResult, ReportSpec } from "../types";
import PrintView from "./PrintView";
import { secondaryButton } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  /** Export a saved, unchanged report by id; otherwise the definition as composed. */
  reportId?: string | null;
  name: string;
  subtitle: string;
  disabled?: boolean;
  onError: (message: string | null) => void;
};

/**
 * Excel and Print/PDF of all rows (at most 10,000), always visible. Without the export permission the buttons
 * stay visible but disabled, with the reason. Every export is recorded in the audit log by the backend.
 */
export default function ExportButtons({ catalog, definition, reportId, name, subtitle, disabled, onError }: Props):
  ReactElement {
  const { t } = useTranslation();
  const canExport = useCan("reports.builder", "export");
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [printing, setPrinting] = useState<ReportResult | null>(null);

  const load = async (format: ExportFormat) => {
    setBusy(format);
    onError(null);
    try {
      const result = await exportReport(reportId ? { reportId, format } : { definition, format });
      if (format === "EXCEL") {
        await downloadReportExcel(t, catalog, definition, result, name);
      } else {
        setPrinting(result);
      }
    } catch (caught) {
      onError(reportErrorMessage(t, caught));
    } finally {
      setBusy(null);
    }
  };

  const reason = !canExport
    ? String(t("reports.builder.export_no_permission", {
      defaultValue: "Exporting needs the permission Reports • Report builder: export. Ask an administrator.",
    }))
    : disabled
      ? String(t("reports.builder.export_fix_first", { defaultValue: "Complete the report first." }))
      : String(t("reports.builder.export_hint", { defaultValue: "All rows, at most 10,000. Exports are logged." }));
  const off = !canExport || disabled || busy !== null;

  return (
    <>
      <button type="button" className={secondaryButton} disabled={off} title={reason} onClick={() => load("EXCEL")}>
        <FaFileExcel aria-hidden /> {busy === "EXCEL"
          ? t("reports.builder.exporting", { defaultValue: "Preparing…" })
          : t("reports.builder.export_excel", { defaultValue: "Excel" })}
      </button>
      <button type="button" className={secondaryButton} disabled={off} title={reason} onClick={() => load("PRINT")}>
        <FaPrint aria-hidden /> {busy === "PRINT"
          ? t("reports.builder.exporting", { defaultValue: "Preparing…" })
          : t("reports.builder.print", { defaultValue: "Print / PDF" })}
      </button>
      {!canExport && <span className="sr-only">{reason}</span>}
      {printing && (
        <PrintView catalog={catalog} definition={definition} result={printing} title={name} subtitle={subtitle}
                   onClose={() => setPrinting(null)} />
      )}
    </>
  );
}
